import { useIsProjectTechnicalWriter } from "@/auth/permissions";
import { useFullReleaseStrict } from "@/common/hooks_useFullRelease";
import { GoalField } from "@/components/GoalField";
import { TriggerIntroduction } from "@/features/triggers/components/TriggerGuidance";
import GoalMenu from "@/pages/models/release/GoalMenu";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import { useTemplateAttributes } from "@/features/release/hooks/useTemplateAttributes";
import { AttributeTemplateField } from "@/ui/forms/attribute-template-field";
import CheckboxField from "@imminently/decisively-ui/CheckboxField";
import Field from "@imminently/decisively-ui/Field";
import { useFormContext, useWatch } from "@imminently/decisively-ui/Form";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import SelectField from "@imminently/decisively-ui/SelectField";
import Tabs from "@imminently/decisively-ui/Tabs";
import TextArea from "@imminently/decisively-ui/TextArea";
import TextField from "@imminently/decisively-ui/TextField";
import { Typography } from "@mui/material";
import { get } from "lodash";
import { useState } from "react";
import { AttributeIntegrationForm } from "./AttributeIntegrationForm";
import { CommunicationFields } from "./CommunicationFields";
import { TriggerScheduling } from "./TriggerScheduling";
import type { AttributeInfoFormValues } from "./useAttributeForm";

export const AttributeTaskForm = ({ scope }: { scope: string }) => {
  const [activeTab, setActiveTab] = useState("task");
  const scopedValue = (name: string) => `${scope}.${name}`;
  const release = useFullReleaseStrict();
  const graph = useParsedGraph();
  const templateAttributes = useTemplateAttributes(graph);
  const form = useFormContext<AttributeInfoFormValues>();
  const values = useWatch({ control: form.control });
  const setFieldValue = (name: string, value: any) =>
    form.setValue(name as any, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  const canEdit = useIsProjectTechnicalWriter();

  const interviews = release.interviews?.map((it) => ({ label: it.name, value: it.id }));
  const useAI = get(values, scopedValue("aiFirstPass"), false);

  // Assignment configuration
  const assignmentMethod = get(values, scopedValue("assignmentMethod")) as
    | "attribute"
    | "integration"
    | "orgmodel"
    | undefined;
  const isIntegrationAssignment = assignmentMethod === "integration";
  const isOrgModelAssignment = assignmentMethod === "orgmodel";
  const assignmentSubmitterField = get(values, scopedValue("assignmentSubmitterField"), "");
  const orgModelSubjectId = get(values, scopedValue("assignmentOrgModel.subjectId"), "");
  const orgModelLevel = get(values, scopedValue("assignmentOrgModel.level"), "");
  const orgModelPreviousLevel = get(values, scopedValue("assignmentOrgModel.previousLevel"), "");
  const orgModelEscalationExhausted = get(values, scopedValue("assignmentOrgModel.escalationExhausted"), "");

  const assignmentMethodOptions = [
    { value: "attribute", label: "Attribute (from rule base)" },
    { value: "integration", label: "Integration (API call)" },
    { value: "orgmodel", label: "Org Models" },
  ];

  // Communication configuration
  const communicationMethod = get(values, scopedValue("communication.method"), "none") as
    | "none"
    | "email"
    | "sms"
    | "proxy";
  const showCommunicationFields = communicationMethod !== "none";

  const communicationMethodOptions = [
    { value: "none", label: "None" },
    { value: "email", label: "Email" },
    { value: "sms", label: "SMS" },
    // { value: "proxy", label: "Proxy" }, // Disabled
  ];

  // Escalation configuration
  const escalationEnabled = get(values, scopedValue("escalation.enabled"), false);
  const escalationMethod = get(values, scopedValue("escalation.communication.method"), "none") as
    | "none"
    | "email"
    | "sms"
    | "proxy";
  const showEscalationFields = escalationEnabled && escalationMethod !== "none";

  const approvalMethodOptions = [
    { value: "any", label: "Any one" },
    { value: "all", label: "All must approve" },
    { value: "majority", label: "Majority" },
    { value: "super-majority", label: "Super Majority" },
  ];

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className="flex flex-col gap-4"
    >
      <TriggerIntroduction type="task" />
      <Tabs.List>
        <Tabs.Trigger value="task">Task</Tabs.Trigger>
        <Tabs.Trigger value="assignment">Assignment</Tabs.Trigger>
        <Tabs.Trigger value="communication">Communication</Tabs.Trigger>
        <Tabs.Trigger value="escalation">Escalation</Tabs.Trigger>
      </Tabs.List>

      {/* Task Tab */}
      <Tabs.Content
        value="task"
        className="flex flex-col gap-4 mt-4"
      >
        <FormPanel>
          <FormPanel>
            <AttributeTemplateField
              label="Title"
              name={scopedValue("title")}
              disabled={!canEdit}
              attributes={templateAttributes.attributes}
              groupOrder={templateAttributes.groupOrder}
              format="text"
              singleLine
              compact
            />
            <SelectField
              label="Interview/Form"
              name={scopedValue("interview")}
              options={interviews}
              disabled={!canEdit}
              fullWidth
            />
          </FormPanel>
        </FormPanel>

        <FormPanel>
          <FormPanel>
            <SelectField
              label="Approval Method"
              name={scopedValue("approvalMethod")}
              options={approvalMethodOptions}
              disabled={!canEdit}
              fullWidth
            />
          </FormPanel>
        </FormPanel>

        <FormPanel>
          <FormPanel>
            <GoalField
              label="Due date"
              name={scopedValue("dueDate")}
              disabled={!canEdit}
            />
          </FormPanel>
        </FormPanel>

        <FormPanel>
          <FormPanel>
            <TextField
              label="Approval label"
              name={scopedValue("approvalLabel")}
              disabled={!canEdit}
              fullWidth
            />
            <TextField
              label="Approval outcome"
              name={scopedValue("approvalOutcome")}
              disabled={!canEdit}
              fullWidth
            />
            <TextField
              label="Rejection label"
              name={scopedValue("rejectionLabel")}
              disabled={!canEdit}
              fullWidth
            />
            <TextField
              label="Rejection outcome"
              name={scopedValue("rejectionOutcome")}
              disabled={!canEdit}
              fullWidth
            />
          </FormPanel>
        </FormPanel>

        <FormPanel>
          <FormPanel>
            <CheckboxField
              label="AI First Pass"
              name={scopedValue("aiFirstPass")}
              disabled={!canEdit}
            />
            {useAI && (
              <TextField
                label="Guidance"
                placeholder="AI guidance prompt..."
                name={scopedValue("guidance")}
                disabled={!canEdit}
                inputComponent={TextArea}
                rows={6}
                fullWidth
              />
            )}
          </FormPanel>
        </FormPanel>

        <FormPanel>
          <FormPanel>
            <TriggerScheduling
              scope={scope}
              disabled={!canEdit}
            />
          </FormPanel>
        </FormPanel>
      </Tabs.Content>

      {/* Assignment Tab */}
      <Tabs.Content
        value="assignment"
        className="flex flex-col gap-4"
      >
        <FormPanel>
          <FormPanel>
            <SelectField
              label="Assignment Method"
              required
              name={scopedValue("assignmentMethod")}
              options={assignmentMethodOptions}
              disabled={!canEdit}
              fullWidth
            />

            {isIntegrationAssignment ? (
              <div className="flex flex-col gap-3">
                <AttributeIntegrationForm
                  scope={scopedValue("assignmentIntegration")}
                  showTitle={false}
                  showScheduling={false}
                  mappingType="assignment"
                />
                <Field fullWidth>
                  <Field.Label>Submitter Field</Field.Label>
                  <Field.Control>
                    <GoalMenu
                      graph={graph}
                      showIdentifiers={true}
                      includeIdentifiers={true}
                      goalId={assignmentSubmitterField}
                      onGoalIdChange={(value: string) => setFieldValue(scopedValue("assignmentSubmitterField"), value)}
                      allowEmpty
                      disabled={!canEdit}
                    />
                  </Field.Control>
                </Field>
              </div>
            ) : isOrgModelAssignment ? (
              <div className="flex flex-col gap-3">
                <Typography
                  variant="body2"
                  color="textSecondary"
                >
                  Resolve assignees from Org Models configured under{" "}
                  <a
                    href="/monitor/contacts/org-models"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Monitor -&gt; Contacts -&gt; Org Models
                  </a>
                  . Subject type selects the relationship hierarchy to use (for example project or position), subject ID
                  identifies which subject to resolve at runtime, level identifies which escalation level to read, and
                  previous escalation level identifies where the last resolved level should be stored.
                </Typography>

                <TextField
                  label="Subject Type"
                  name={scopedValue("assignmentOrgModel.subjectType")}
                  disabled={!canEdit}
                  fullWidth
                />

                <Field fullWidth>
                  <Field.Label>Subject ID</Field.Label>
                  <Field.Control>
                    <GoalMenu
                      graph={graph}
                      showIdentifiers={true}
                      includeIdentifiers={true}
                      goalId={orgModelSubjectId}
                      onGoalIdChange={(value: string) =>
                        setFieldValue(scopedValue("assignmentOrgModel.subjectId"), value)
                      }
                      allowEmpty
                      disabled={!canEdit}
                    />
                  </Field.Control>
                </Field>

                <Field fullWidth>
                  <Field.Label>Level</Field.Label>
                  <Field.Control>
                    <GoalMenu
                      graph={graph}
                      showIdentifiers={true}
                      includeIdentifiers={true}
                      goalId={orgModelLevel}
                      onGoalIdChange={(value: string) => setFieldValue(scopedValue("assignmentOrgModel.level"), value)}
                      allowEmpty
                      disabled={!canEdit}
                    />
                  </Field.Control>
                </Field>

                <Field fullWidth>
                  <Field.Label>Previous Escalation Level</Field.Label>
                  <Field.Control>
                    <GoalMenu
                      graph={graph}
                      showIdentifiers={true}
                      includeIdentifiers={true}
                      goalId={orgModelPreviousLevel}
                      onGoalIdChange={(value: string) =>
                        setFieldValue(scopedValue("assignmentOrgModel.previousLevel"), value)
                      }
                      allowEmpty
                      disabled={!canEdit}
                    />
                  </Field.Control>
                </Field>

                <Field>
                  <Field.Label>Escalation Exhausted</Field.Label>
                  <Field.Control>
                    <GoalMenu
                      graph={graph}
                      showIdentifiers={true}
                      includeIdentifiers={true}
                      goalId={orgModelEscalationExhausted}
                      onGoalIdChange={(value: string) => setFieldValue(scopedValue("assignmentOrgModel.escalationExhausted"), value)}
                      allowEmpty
                      disabled={!canEdit}
                    />
                  </Field.Control>
                </Field>

                <Field fullWidth>
                  <Field.Label>Submitter Field</Field.Label>
                  <Field.Control>
                    <GoalMenu
                      graph={graph}
                      showIdentifiers={true}
                      includeIdentifiers={true}
                      goalId={assignmentSubmitterField}
                      onGoalIdChange={(value: string) => setFieldValue(scopedValue("assignmentSubmitterField"), value)}
                      allowEmpty
                      disabled={!canEdit}
                    />
                  </Field.Control>
                </Field>

                <Typography
                  variant="body2"
                  color="textSecondary"
                >
                  Use model logic to change level over time or by condition as needed.
                </Typography>
              </div>
            ) : (
              <GoalField
                label="Assignment"
                name={scopedValue("assignment")}
                disabled={!canEdit}
              />
            )}
          </FormPanel>
        </FormPanel>
      </Tabs.Content>

      {/* Communication Tab */}
      <Tabs.Content
        value="communication"
        className="flex flex-col gap-4 mt-4"
      >
        <FormPanel>
          <FormPanel>
            <Typography
              variant="body2"
              color="textSecondary"
            >
              Optionally send an email or SMS when someone is assigned this task.
            </Typography>

            <SelectField
              label="Communication Method"
              name={scopedValue("communication.method")}
              options={communicationMethodOptions}
              disabled={!canEdit}
              fullWidth
            />

            {showCommunicationFields && (
              <CommunicationFields
                scope={scopedValue("communication")}
                hideWho={true}
                whoMessage="Message (will be sent to task assignees)"
              />
            )}
          </FormPanel>
        </FormPanel>
      </Tabs.Content>

      {/* Escalation Tab */}
      <Tabs.Content
        value="escalation"
        className="flex flex-col gap-4 mt-4"
      >
        <FormPanel>
          <FormPanel>
            <Typography
              variant="body2"
              color="textSecondary"
            >
              Configure automatic escalation if the task is not completed on time. The communication below will be sent
              to any new assignees due to escalation. It will be sent each time you escalate.
            </Typography>

            <CheckboxField
              label="Enable Escalation"
              name={scopedValue("escalation.enabled")}
              disabled={!canEdit}
            />

            {escalationEnabled && (
              <>
                <GoalField
                  label="Escalation time attribute"
                  name={scopedValue("escalation.attribute")}
                  disabled={!canEdit}
                />

                <SelectField
                  label="Communication Method"
                  name={scopedValue("escalation.communication.method")}
                  options={communicationMethodOptions}
                  disabled={!canEdit}
                  fullWidth
                />

                {showEscalationFields && (
                  <CommunicationFields
                    scope={scopedValue("escalation.communication")}
                    hideWho={true}
                    whoMessage="Message (will be sent to escalated assignees)"
                  />
                )}
              </>
            )}
          </FormPanel>
        </FormPanel>
      </Tabs.Content>
    </Tabs>
  );
};
